import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { Refresh, VideoCameraFront } from "@mui/icons-material";
import dayjs from "dayjs";
import type { TeleconsultoriaReceivedInvite } from "../../types";
import { groupReceivedInvites } from "../../utils/groupReceivedInvites";
import { ReceivedInvitesGroup } from "./ReceivedInvitesGroup";

/** Re-agrupa periodicamente: um convite muda de grupo sem precisar atualizar. */
const REGROUP_INTERVAL_MS = 60_000;

interface ReceivedInvitesListProps {
  invites: TeleconsultoriaReceivedInvite[];
  loading: boolean;
  /** Disponibilidade do profissional: usada só na dica do estado vazio. */
  available: boolean;
  onRefresh: () => void;
  lastUpdatedAt?: Date | null;
  refreshDisabled?: boolean;
}

/** Bloco "Convites recebidos": cabeçalho, estados e grupos por momento. */
export function ReceivedInvitesList({
  invites,
  loading,
  available,
  onRefresh,
  lastUpdatedAt,
  refreshDisabled = false,
}: ReceivedInvitesListProps) {
  const [now, setNow] = useState(() => dayjs());

  useEffect(() => {
    const id = window.setInterval(() => setNow(dayjs()), REGROUP_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, []);

  // Lista nova: agrupa com o horário atual, sem esperar o próximo ciclo.
  useEffect(() => {
    setNow(dayjs());
  }, [invites]);

  const groups = useMemo(() => groupReceivedInvites(invites, now), [invites, now]);
  const firstLoad = loading && invites.length === 0;

  return (
    <Paper variant="outlined" sx={{ p: 2, position: "relative", overflow: "hidden" }}>
      {/* Atualização com lista já carregada: barra fina, sem esconder os cards. */}
      {loading && invites.length > 0 && (
        <LinearProgress sx={{ position: "absolute", top: 0, left: 0, right: 0, height: 3 }} />
      )}

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        useFlexGap
        gap={1}
        sx={{ mb: 2 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle1" fontWeight={700}>
            Convites recebidos
          </Typography>
          <Chip
            size="small"
            label={invites.length}
            color={invites.length ? "primary" : "default"}
            sx={{ height: 22 }}
          />
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          {lastUpdatedAt && (
            <Typography variant="caption" color="text.secondary">
              Atualizado às {dayjs(lastUpdatedAt).format("HH:mm")}
            </Typography>
          )}
          <Button
            size="small"
            startIcon={loading ? <CircularProgress size={16} /> : <Refresh />}
            onClick={onRefresh}
            disabled={loading || refreshDisabled}
            sx={{ textTransform: "none" }}
          >
            Atualizar
          </Button>
        </Stack>
      </Stack>

      {firstLoad ? (
        <Stack spacing={1} aria-busy="true" aria-label="Carregando convites">
          {[0, 1, 2].map((item) => (
            <Skeleton key={item} variant="rounded" height={84} />
          ))}
        </Stack>
      ) : invites.length === 0 ? (
        <Stack alignItems="center" spacing={1} sx={{ py: 4, textAlign: "center" }}>
          <VideoCameraFront aria-hidden sx={{ fontSize: 48, color: "text.disabled" }} />
          <Typography variant="body1" fontWeight={600}>
            Nenhum convite no momento
          </Typography>
          {!available && (
            <Typography variant="body2" color="text.secondary">
              Ligue a disponibilidade para receber convites.
            </Typography>
          )}
        </Stack>
      ) : (
        <Stack spacing={3}>
          {groups.map((group) => (
            <ReceivedInvitesGroup key={group.key} group={group} now={now} />
          ))}
        </Stack>
      )}
    </Paper>
  );
}
